<template>
  <div class="sf-el kvk-input">
    <div class="sf-kvk">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>

      <div class="sf-kvk__search-container">
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="placeholder || 'Search for a company...'"
          @input="handleSearch"
          @focus="showDropdown = true"
          @blur="handleBlur"
          class="sf-kvk__input"
          :class="{ 'sf-kvk__input--selected': selectedCompany }"
        />

        <div class="sf-kvk__selected" v-if="selectedCompany && !isSearching">
          <div class="sf-kvk__selected-info">
            <div class="sf-kvk__selected-name">{{ selectedCompany.name }}</div>
            <div class="sf-kvk__selected-details">
              KvK: {{ selectedCompany.kvkNumber }}
              <span v-if="selectedCompany.address">
                | {{ selectedCompany.address.fullAddress }}
              </span>
              <span v-if="selectedCompany.enriched" class="sf-kvk__enriched-badge">✓ Enriched</span>
            </div>
          </div>
          <button type="button" @click="clearSelection" class="sf-kvk__clear-btn" tabindex="-1">
            ×
          </button>
        </div>

        <div
          v-if="showDropdown && (searchResults.length > 0 || isLoading || error)"
          class="sf-kvk__dropdown"
        >
          <!-- Loading state -->
          <div v-if="isLoading" class="sf-kvk__loading">
            <div class="sf-kvk__spinner"></div>
            <span v-if="selectedCompany">Enriching company data...</span>
            <span v-else>Searching companies...</span>
          </div>

          <!-- Error state -->
          <div v-else-if="error" class="sf-kvk__error">
            {{ error }}
          </div>

          <!-- Results -->
          <div v-else-if="searchResults.length > 0">
            <div
              v-for="company in searchResults"
              :key="company.kvkNumber"
              @mousedown="selectCompany(company)"
              class="sf-kvk__result-item"
              :class="{ 'sf-kvk__result-item--enrichable': company.vestigingsnummer }"
            >
              <div class="sf-kvk__result-name">
                {{ company.name }}
                <span v-if="company.vestigingsnummer" class="sf-kvk__enrichable-indicator">⚡</span>
              </div>
              <div class="sf-kvk__result-details">
                KvK: {{ company.kvkNumber }} | {{ company.address }}
              </div>
            </div>
          </div>

          <!-- No results -->
          <div v-else-if="searchQuery.length > 2" class="sf-kvk__no-results">
            No companies found for "{{ searchQuery }}"
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { watch, ref, computed } from 'vue';
  import { kvkService } from '@shared/services/kvkService.js';

  export default {
    name: 'KvkElement',
    props: {
      label: String,
      description: String,
      caption: String,
      name: String,
      modelValue: [String, Object],
      required: Boolean,
      placeholder: String,
      element: {
        type: Object,
        required: true,
      },
    },
    emits: ['update:modelValue', 'dataEnriched'],
    setup(props, { emit }) {
      const searchQuery = ref('');
      const searchResults = ref([]);
      const selectedCompany = ref(null);
      const showDropdown = ref(false);
      const isLoading = ref(false);
      const error = ref('');
      const searchTimeout = ref(null);

      // Computed to determine if we're currently searching
      const isSearching = computed(() => searchQuery.value.length > 0 && !selectedCompany.value);

      // Initialize from modelValue
      if (props.modelValue) {
        if (typeof props.modelValue === 'object') {
          selectedCompany.value = props.modelValue;
          searchQuery.value = props.modelValue.name || '';
        } else {
          // If it's a string, it might be just the KvK number
          searchQuery.value = props.modelValue;
        }
      }

      const handleSearch = () => {
        if (selectedCompany.value) {
          // Clear selection when user starts typing
          selectedCompany.value = null;
          emit('update:modelValue', null);
        }

        // Clear previous timeout
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }

        // Don't search for very short queries
        if (searchQuery.value.length < 3) {
          searchResults.value = [];
          showDropdown.value = false;
          return;
        }

        // Debounce the search
        searchTimeout.value = setTimeout(() => {
          performSearch();
        }, 300);
      };

      const performSearch = async () => {
        if (searchQuery.value.length < 3) return;

        isLoading.value = true;
        error.value = '';
        showDropdown.value = true;

        try {
          const data = await kvkService.searchCompanies(searchQuery.value);
          searchResults.value = data.results || [];
          if (searchResults.value.length === 0) {
            error.value = '';
          }
        } catch (err) {
          console.error('KvK search error:', err);
          error.value = err.message || 'Failed to search companies. Please try again.';
          searchResults.value = [];
        } finally {
          isLoading.value = false;
        }
      };

      const selectCompany = async (company) => {
        try {
          // Show loading state while enriching
          isLoading.value = true;
          error.value = '';

          // First set basic company data
          selectedCompany.value = company;
          searchQuery.value = company.name;
          searchResults.value = [];
          showDropdown.value = false;

          // Enrich the company data if vestigingsnummer is available
          let enrichedCompany = company;
          if (company.vestigingsnummer) {
            try {
              const enrichedData = await kvkService.enrichCompanyProfile(company.vestigingsnummer);
              // Get 'bezoekadres'
              const visitingAddress = enrichedData.addresses?.find(
                (addr) => addr.type === 'bezoekadres'
              );

              // Fallback to the first address if 'bezoekadres' is not available
              const addressToUse = visitingAddress || enrichedData.addresses?.[0];

              // Merge enriched data with basic company data
              enrichedCompany = {
                ...company,
                address: addressToUse || company.address,
                contactInfo: {
                  ...company.contactInfo,
                  website: enrichedData.websites?.[0] || '',
                },
                businessInfo: {
                  ...company.businessInfo,
                  name: enrichedData.tradeNames?.[0]?.name || enrichedData.name,
                  kvkNumber: enrichedData.kvkNumber,
                  sbiActivities: enrichedData.sbiActivities,
                  employees: enrichedData.employees,
                  isNonMailing: enrichedData.isNonMailing,
                },
                enriched: true,
              };

              // First update the KVK element's own value
              selectedCompany.value = enrichedCompany;
              emit('update:modelValue', enrichedCompany);

              // Then trigger the data enrichment for mapped fields
              emit('dataEnriched', enrichedCompany);
            } catch (enrichError) {
              console.warn('Failed to enrich company data, using basic data:', enrichError);
              // If enrichment fails, fall back to basic update
              selectedCompany.value = company;
              emit('update:modelValue', company);
            }
          } else {
            // For non-enrichable companies, just do the basic update
            selectedCompany.value = company;
            emit('update:modelValue', company);
          }
        } catch (err) {
          console.error('Error selecting company:', err);
          error.value = 'Failed to select company. Please try again.';
        } finally {
          isLoading.value = false;
        }
      };

      const clearSelection = () => {
        selectedCompany.value = null;
        searchQuery.value = '';
        searchResults.value = [];
        showDropdown.value = false;
        emit('update:modelValue', null);
      };

      const handleBlur = () => {
        // Delay hiding dropdown to allow click events to fire
        setTimeout(() => {
          showDropdown.value = false;
        }, 200);
      };

      // Get display address with priority for enriched data
      const getDisplayAddress = (company) => {
        if (!company) return '';

        // Use enriched address if available and has full details
        if (company.address && typeof company.address === 'object' && company.address.full) {
          return company.address.full;
        }

        // Fallback to basic address string
        if (typeof company.address === 'string') {
          return company.address;
        }

        return '';
      };

      // Watch for external changes to modelValue
      watch(
        () => props.modelValue,
        (newValue) => {
          if (!newValue) {
            selectedCompany.value = null;
            searchQuery.value = '';
          } else if (typeof newValue === 'object' && newValue !== selectedCompany.value) {
            selectedCompany.value = newValue;
            searchQuery.value = newValue.name || '';
          }
        }
      );

      return {
        searchQuery,
        searchResults,
        selectedCompany,
        showDropdown,
        isLoading,
        error,
        isSearching,
        handleSearch,
        selectCompany,
        clearSelection,
        handleBlur,
        getDisplayAddress,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sf-kvk {
    position: relative;

    &__search-container {
      position: relative;
    }

    &__input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--gray-300);
      border-radius: 0.375rem;
      font-size: 1rem;
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

      &:focus {
        outline: none;
        border-color: var(--blue-500);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &--selected {
        display: none;
      }
    }

    &__selected {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem;
      border: 1px solid var(--cs-brand-primary);
      border-radius: 0.375rem;
      background-color: var(--cs-input-default-background);
      margin-top: 0.5rem;

      &-info {
        flex: 1;
      }

      &-name {
        font-weight: 600;
        color: var(--cs-brand-primary);
        margin-bottom: 0.25rem;
      }

      &-details {
        font-size: 0.875rem;
        color: var(--cs-input-default-text);
      }
    }

    &__enriched-badge {
      display: inline-block;
      margin-left: 0.5rem;
      padding: 0.125rem 0.375rem;
      background-color: var(--cs-brand-primary);
      color: var(--cs-button-default-text);
      font-size: 0.75rem;
      font-weight: 500;
      border-radius: 0.25rem;
      opacity: 0.8;
    }

    &__clear-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: var(--cs-brand-primary);
      cursor: pointer;
      padding: 0.25rem;
      line-height: 1;

      &:hover {
        opacity: 0.8;
      }
    }

    &__dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: var(--cs-input-default-background);
      border: 1px solid var(--cs-input-default-border);
      border-top: none;
      border-radius: 0 0 0.375rem 0.375rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      max-height: 200px;
      overflow-y: auto;
      z-index: 2000;
    }

    &__loading {
      padding: 1rem;
      text-align: center;
      color: var(--gray-600);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    &__spinner {
      width: 1rem;
      height: 1rem;
      border: 2px solid var(--gray-200);
      border-top: 2px solid var(--blue-500);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    &__error {
      padding: 1rem;
      color: var(--red-600);
      text-align: center;
    }

    &__result-item {
      padding: 0.75rem;
      cursor: pointer;
      border-bottom: 1px solid var(--gray-100);

      &:hover {
        background-color: var(--gray-50);
      }

      &:last-child {
        border-bottom: none;
      }

      &--enrichable {
        position: relative;

        &:hover {
          background-color: var(--cs-input-default-background);
        }
      }
    }

    &__enrichable-indicator {
      margin-left: 0.5rem;
      font-size: 0.875rem;
      opacity: 0.7;
    }

    &__result-name {
      font-weight: 600;
      color: var(--gray-900);
      margin-bottom: 0.25rem;
    }

    &__result-details {
      font-size: 0.875rem;
      color: var(--gray-600);
    }

    &__no-results {
      padding: 1rem;
      text-align: center;
      color: var(--gray-500);
      font-style: italic;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
